import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { Badge, Button, Icon, PageHeader } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { adminApi } from "../api";
import { useAdminResource } from "../hooks";
import { ActionNotice, useDialogs } from "../dialogs";
import { DataTable, DataRow, EmptyHint, ErrorState, LoadingState, RowAction } from "../components";
import type { AdminUserRow, UserAppSubscription } from "../types";

const planTone: Record<string, "purple" | "blue" | "neutral"> = { pro: "purple", student: "blue", free: "neutral" };

const APPS = [
  { slug: "nelurio", label: "Nelurio" },
  { slug: "finello", label: "Finello" },
  { slug: "nexdeutsch", label: "NexDeutsch" },
  { slug: "washhalle", label: "Washhalle" }
];

function appLabel(t: (key: string) => string, slug: string): string {
  return APPS.find((app) => app.slug === slug)?.label ?? slug;
}

/** Whole remaining days (rounding up) until an ISO date, or null when absent/invalid. */
function daysLeft(until: string | null): number | null {
  if (!until) return null;
  const end = new Date(until).getTime();
  if (!Number.isFinite(end)) return null;
  return Math.max(0, Math.ceil((end - Date.now()) / 86400000));
}

/** Status chip for one subscription: green Premium, blue Trial + days, gray Abgelaufen. */
function AppChip({ sub }: { sub: UserAppSubscription }) {
  const { t, formatDate } = useI18n();
  const until = sub.until ? formatDate(sub.until) : "";
  if (sub.status === "premium") {
    return (
      <span title={until ? t("admin.users.premiumUntil", { until }) : undefined}>
        <Badge tone="purple">Premium</Badge>
      </span>
    );
  }
  if (sub.status === "trial") {
    const days = daysLeft(sub.until);
    const dayText = days === null ? "" : days === 1 ? t("admin.users.trialOneDay") : t("admin.users.trialDays", { days });
    return (
      <span title={until ? t("admin.users.trialUntil", { until }) : undefined}>
        <Badge tone="blue">{dayText ? t("admin.users.trialBadge", { days: dayText }) : "Trial"}</Badge>
      </span>
    );
  }
  return (
    <span title={until ? t("admin.users.endedOn", { until }) : undefined}>
      <Badge tone="coral">{t("admin.users.expired")}</Badge>
    </span>
  );
}

/** Apps cell: one line per product with its status chip and a revoke button on active premium. */
function AppsCell({ user, onRevoke }: { user: AdminUserRow; onRevoke: (user: AdminUserRow, sub: UserAppSubscription) => void }) {
  const { t } = useI18n();
  if (!user.apps || user.apps.length === 0) return <span className="admin-apps-empty">{t("admin.users.noApps")}</span>;
  return (
    <span className="admin-apps-cell">
      {user.apps.map((sub) => (
        <span className="admin-app-sub" key={sub.app}>
          <span className="admin-app-sub__name">{appLabel(t, sub.app)}</span>
          <AppChip sub={sub} />
          {sub.status === "premium" && (
            <button
              type="button"
              className="admin-app-sub__revoke"
              title={t("admin.users.revokePremiumTitle", { app: appLabel(t, sub.app) })}
              aria-label={t("admin.users.revokePremiumAria", { app: appLabel(t, sub.app), email: user.email })}
              onClick={() => onRevoke(user, sub)}
            >
              <Icon name="x" size={12} />
            </button>
          )}
        </span>
      ))}
    </span>
  );
}

/** Inline dialog to grant premium: product select + months (1 to 120, default 12). */
function GrantPremiumDialog({ user, onClose, onGranted, onFailed }: {
  user: AdminUserRow;
  onClose: () => void;
  onGranted: (info: string) => void;
  onFailed: (message: string) => void;
}) {
  const { t } = useI18n();
  const [appSlug, setAppSlug] = useState(APPS[0].slug);
  const [months, setMonths] = useState("12");
  const [fieldError, setFieldError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    const parsed = Number.parseInt(months, 10);
    if (String(parsed) !== months.trim() || parsed < 1 || parsed > 120) {
      setFieldError(t("admin.users.grantMonthsError"));
      return;
    }
    setBusy(true);
    try {
      await adminApi(`/users/${user.id}/premium`, { method: "POST", body: JSON.stringify({ appSlug, months: parsed }) });
      onGranted(t("admin.users.grantSuccess", { email: user.email, app: appLabel(t, appSlug), months: parsed }));
    } catch (reason) {
      setBusy(false);
      onFailed(reason instanceof Error ? reason.message : t("admin.users.actionFailed"));
    }
  };

  return (
    <div className="admin-modal" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="admin-modal__card" role="dialog" aria-modal="true" aria-label={t("admin.users.grantTitle")}>
        <h2>{t("admin.users.grantTitle")}</h2>
        <p className="admin-modal__message">{t("admin.users.grantMessage", { displayName: user.displayName, email: user.email })}</p>
        <label className="nl-field">
          <span className="nl-field__label">{t("admin.users.fieldProduct")}</span>
          <select className="nl-input nl-select" value={appSlug} onChange={(event) => setAppSlug(event.target.value)}>
            {APPS.map((app) => <option key={app.slug} value={app.slug}>{app.label}</option>)}
          </select>
        </label>
        <label className="nl-field">
          <span className="nl-field__label">{t("admin.users.fieldMonths")}</span>
          <input
            className={`nl-input ${fieldError ? "nl-input--error" : ""}`}
            aria-invalid={!!fieldError || undefined}
            type="number"
            min={1}
            max={120}
            step={1}
            inputMode="numeric"
            value={months}
            autoFocus
            onChange={(event) => { setMonths(event.target.value); setFieldError(""); }}
            onKeyDown={(event) => { if (event.key === "Enter") void submit(); }}
          />
          {fieldError && <span className="nl-field__hint nl-field__hint--error">{fieldError}</span>}
        </label>
        <div className="admin-modal__actions">
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button loading={busy} onClick={() => void submit()}>{t("admin.users.grantTitle")}</Button>
        </div>
      </div>
    </div>
  );
}

export function UsersPage() {
  const { t, formatDate } = useI18n();
  const dialogs = useDialogs();
  const [appFilter, setAppFilter] = useState("");
  const users = useAdminResource<AdminUserRow[]>(appFilter ? `/users?limit=100&app=${appFilter}` : "/users?limit=100");
  const [notice, setNotice] = useState<{ error?: string; info?: string }>({});
  const [grantUser, setGrantUser] = useState<AdminUserRow | null>(null);
  const [detail, setDetail] = useState<AdminUserRow | null>(null);
  const [params, setParams] = useSearchParams();

  const setStatus = async (user: AdminUserRow) => {
    const suspend = user.status === "active";
    const confirmed = await dialogs.confirm({
      title: suspend ? t("admin.users.suspendTitle") : t("admin.users.reactivateTitle"),
      message: suspend
        ? t("admin.users.suspendMessage", { displayName: user.displayName, email: user.email })
        : t("admin.users.reactivateMessage", { displayName: user.displayName, email: user.email }),
      confirmLabel: suspend ? t("admin.users.suspendConfirm") : t("admin.users.reactivateConfirm"),
      danger: suspend
    });
    if (!confirmed) return;
    try {
      await adminApi(`/users/${user.id}/status`, { method: "POST", body: JSON.stringify({ status: suspend ? "suspended" : "active" }) });
      setNotice({ info: suspend ? t("admin.users.suspendedNotice", { email: user.email }) : t("admin.users.activatedNotice", { email: user.email }) });
      users.reload();
    } catch (reason) {
      setNotice({ error: reason instanceof Error ? reason.message : t("admin.users.actionFailed") });
    }
  };

  const setTempPassword = async (user: AdminUserRow) => {
    const password = await dialogs.prompt({
      title: t("admin.users.tempPwTitle"),
      message: t("admin.users.tempPwMessage", { email: user.email }),
      label: t("admin.users.tempPwLabel"),
      inputType: "text",
      hint: t("admin.users.tempPwHint"),
      validate: (value) => (value.trim().length >= 8 ? "" : t("admin.users.tempPwError")),
      submitLabel: t("admin.users.tempPwSubmit")
    });
    if (password === null) return;
    try {
      await adminApi(`/users/${user.id}/temp-password`, { method: "POST", body: JSON.stringify({ password }) });
      setNotice({ info: t("admin.users.tempPwNotice", { email: user.email }) });
    } catch (reason) {
      setNotice({ error: reason instanceof Error ? reason.message : t("admin.users.actionFailed") });
    }
  };

  const deleteUser = async (user: AdminUserRow) => {
    const confirmed = await dialogs.confirm({
      title: t("admin.users.deleteTitle"),
      message: t("admin.users.deleteMessage", { displayName: user.displayName, email: user.email }),
      confirmLabel: t("admin.users.deleteConfirm"),
      danger: true
    });
    if (!confirmed) return;
    try {
      await adminApi(`/users/${user.id}`, { method: "DELETE" });
      setNotice({ info: t("admin.users.deletedNotice", { email: user.email }) });
      users.reload();
    } catch (reason) {
      setNotice({ error: reason instanceof Error ? reason.message : t("admin.users.actionFailed") });
    }
  };

  const revokePremium = async (user: AdminUserRow, sub: UserAppSubscription) => {
    const label = appLabel(t, sub.app);
    const confirmed = await dialogs.confirm({
      title: t("admin.users.revokeTitle"),
      message: t("admin.users.revokeMessage", { displayName: user.displayName, email: user.email, app: label }),
      confirmLabel: t("admin.users.revokeConfirm"),
      danger: true
    });
    if (!confirmed) return;
    try {
      await adminApi(`/users/${user.id}/revoke`, { method: "POST", body: JSON.stringify({ appSlug: sub.app }) });
      setNotice({ info: t("admin.users.revokedNotice", { email: user.email, app: label }) });
      users.reload();
    } catch (reason) {
      setNotice({ error: reason instanceof Error ? reason.message : t("admin.users.actionFailed") });
    }
  };

  // Command-palette jump: /users?focus=<email> opens that user's drawer.
  useEffect(() => {
    const focusEmail = params.get("focus");
    if (!focusEmail) return;
    const match = (users.data ?? []).find((u) => u.email.toLowerCase() === focusEmail.toLowerCase());
    if (match) setDetail(match);
    setParams({}, { replace: true });
  }, [params, users.data, setParams]);

  // Escape closes the user drawer (same global pattern as the grant modal).
  useEffect(() => {
    if (!detail) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDetail(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detail]);

  const rows = users.data ?? [];
  const labels = [t("admin.users.colUser"), t("admin.plan"), t("admin.users.colApps"), t("admin.users.colLanguage"), t("admin.lastActive"), t("admin.status"), t("admin.users.colActions")];
  const columns = [
    { label: labels[0], width: "minmax(180px, 1.4fr)" },
    { label: labels[1], width: "minmax(84px, .6fr)" },
    { label: labels[2], width: "minmax(170px, 1.3fr)" },
    { label: labels[3], width: "minmax(80px, .5fr)" },
    { label: labels[4], width: "minmax(120px, .9fr)" },
    { label: labels[5], width: "minmax(96px, .6fr)" },
    { label: labels[6], width: "minmax(130px, .8fr)" }
  ];

  return (
    <div className="admin-page">
      <PageHeader title={t("admin.users")} subtitle={t("admin.users.subtitle")} />
      <ActionNotice error={notice.error} info={notice.info} onDismiss={() => setNotice({})} />

      {users.status === "error" && <ErrorState message={users.error} onRetry={() => void users.reload()} />}
      {users.status === "loading" && <LoadingState rows={5} />}
      {users.status === "ready" && (
        rows.length === 0
          ? <EmptyHint>{appFilter ? t("admin.users.emptyFiltered", { app: appLabel(t, appFilter) }) : t("admin.users.empty")}</EmptyHint>
          : <>
            <div className="admin-filter" role="group" aria-label={t("admin.users.filterByProduct")}>
              <button
                type="button"
                className={`admin-filter__chip${appFilter === "" ? " is-active" : ""}`}
                aria-pressed={appFilter === ""}
                onClick={() => setAppFilter("")}
              >
                {t("admin.users.allProducts")}
              </button>
              {APPS.map((app) => (
                <button
                  key={app.slug}
                  type="button"
                  className={`admin-filter__chip${appFilter === app.slug ? " is-active" : ""}`}
                  aria-pressed={appFilter === app.slug}
                  onClick={() => setAppFilter(app.slug)}
                >
                  {app.label}
                </button>
              ))}
            </div>
            <DataTable columns={columns} minWidth={900}>
              {rows.map((user) => (
                <DataRow key={user.id} labels={labels}>
                  <button type="button" className="admin-person admin-person--link" onClick={() => setDetail(user)} title={t("admin.users.detailAria")}>
                    <i aria-hidden="true">{(user.displayName || "?").slice(0, 1).toUpperCase()}</i>
                    <span className="admin-person__meta">
                      <strong>{user.displayName}</strong>
                      <small className="admin-wrap">{user.email}</small>
                    </span>
                  </button>
                  <Badge tone={planTone[user.plan] ?? "neutral"}>{user.plan === "pro" ? "Pro" : user.plan === "student" ? "Student" : user.plan === "free" ? "Free" : user.plan}</Badge>
                  <AppsCell user={user} onRevoke={(target, sub) => void revokePremium(target, sub)} />
                  <span>{user.locale || t("admin.common.missing")}</span>
                  <span className="admin-time">{user.lastActiveAt ? formatDate(user.lastActiveAt) : t("admin.users.never")}</span>
                  <Badge tone={user.status === "active" ? "green" : "coral"}>{user.status === "active" ? t("admin.users.statusActive") : user.status === "suspended" ? t("admin.users.statusSuspended") : user.status}</Badge>
                  <span className="admin-row-actions">
                    <RowAction label={t("admin.users.grantTitle")} icon="plus" onClick={() => setGrantUser(user)} />
                    <RowAction
                      label={user.status === "active" ? t("admin.users.suspendTitle") : t("admin.users.reactivateTitle")}
                      icon="lock"
                      onClick={() => void setStatus(user)}
                    />
                    <RowAction label={t("admin.users.tempPwTitle")} icon="edit" onClick={() => void setTempPassword(user)} />
                    <RowAction label={t("admin.users.deleteTitle")} icon="trash" danger onClick={() => void deleteUser(user)} />
                  </span>
                </DataRow>
              ))}
            </DataTable>
            {rows.length >= 100 && <p className="admin-limit-note">{t("admin.users.limitNote")}</p>}
          </>
      )}
      {detail && (
        <div className="admin-drawer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setDetail(null); }}>
          <aside className="admin-drawer__card" role="dialog" aria-modal="true" aria-label={t("admin.users.detailAria")}>
            <div className="admin-drawer__head">
              <span className="admin-person">
                <i aria-hidden="true">{(detail.displayName || "?").slice(0, 1).toUpperCase()}</i>
                <span className="admin-person__meta">
                  <strong>{detail.displayName}</strong>
                  <small className="admin-wrap">{detail.email}</small>
                </span>
              </span>
              <button type="button" className="admin-drawer__close" aria-label={t("common.close")} onClick={() => setDetail(null)}><Icon name="x" size={18} /></button>
            </div>
            <div className="admin-drawer__body">
              <dl className="admin-drawer__facts">
                <div><dt>{t("admin.plan")}</dt><dd>{detail.plan}</dd></div>
                <div><dt>{t("admin.status")}</dt><dd><Badge tone={detail.status === "active" ? "green" : "coral"}>{detail.status === "active" ? t("admin.users.statusActive") : detail.status === "suspended" ? t("admin.users.statusSuspended") : detail.status}</Badge></dd></div>
                <div><dt>{t("admin.users.colLanguage")}</dt><dd>{detail.locale || t("admin.common.missing")}</dd></div>
                <div><dt>{t("admin.lastActive")}</dt><dd>{detail.lastActiveAt ? formatDate(detail.lastActiveAt) : t("admin.users.never")}</dd></div>
              </dl>
              <h3>{t("admin.users.colApps")}</h3>
              <div className="admin-drawer__apps">
                {(detail.apps ?? []).length === 0 && <p className="nlp-hint">{t("admin.users.noApps")}</p>}
                {(detail.apps ?? []).map((sub) => (
                  <div className="admin-drawer__app" key={sub.app}>
                    <span className="admin-app-sub__name">{appLabel(t, sub.app)}</span>
                    <AppChip sub={sub} />
                    {sub.status === "premium" && (
                      <button type="button" className="admin-drawer__apprevoke" onClick={() => { setDetail(null); void revokePremium(detail, sub); }}>{t("admin.users.revokeTitle")}</button>
                    )}
                  </div>
                ))}
              </div>
              <h3>{t("admin.users.detailActions")}</h3>
              <div className="admin-drawer__actions">
                <Button icon="plus" onClick={() => { setDetail(null); setGrantUser(detail); }}>{t("admin.users.grantTitle")}</Button>
                <Button variant="secondary" icon="lock" onClick={() => { setDetail(null); void setStatus(detail); }}>{detail.status === "active" ? t("admin.users.suspendTitle") : t("admin.users.reactivateTitle")}</Button>
                <Button variant="secondary" icon="edit" onClick={() => { setDetail(null); void setTempPassword(detail); }}>{t("admin.users.tempPwTitle")}</Button>
                <Button variant="danger" icon="trash" onClick={() => { setDetail(null); void deleteUser(detail); }}>{t("admin.users.deleteTitle")}</Button>
              </div>
            </div>
          </aside>
        </div>
      )}
      {grantUser && (
        <GrantPremiumDialog
          user={grantUser}
          onClose={() => setGrantUser(null)}
          onGranted={(info) => {
            setGrantUser(null);
            setNotice({ info });
            users.reload();
          }}
          onFailed={(message) => setNotice({ error: message })}
        />
      )}
    </div>
  );
}
